/**
 * JSON-LD (schema.org) builders for SEO + GEO on the race microsite.
 *
 * Generative engines (ChatGPT, Perplexity, Google AI Overviews) lean heavily on
 * structured data to extract and cite facts. These builders emit plain objects
 * that `useDocumentMeta({ jsonLd })` serializes into <script type="application/ld+json">.
 */
import {
  SITE_URL,
  SITE_NAME,
  absoluteUrl,
  ORG_NAME,
  ORG_CONTACT_EMAIL,
  ORG_LOGO_URL,
  SITE_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  EVENT_START_ISO,
  EVENT_END_ISO,
  EVENT_VENUE_NAME,
  EVENT_STREET_ADDRESS,
  EVENT_LOCALITY,
  EVENT_POSTAL_CODE,
  EVENT_COUNTRY,
  BRAND_ALTERNATE_NAMES,
  BRAND_SAME_AS,
} from "./site";

type Json = Record<string, unknown>;

const ORG_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;
const EVENT_ID = `${SITE_URL}/#event`;

/** schema.org/Organization — the event brand (jointly presented by Bursa Malaysia & The Edge). */
export function organizationLd(): Json {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: ORG_NAME,
    alternateName: BRAND_ALTERNATE_NAMES,
    url: SITE_URL,
    logo: ORG_LOGO_URL,
    description: SITE_DESCRIPTION,
    email: ORG_CONTACT_EMAIL,
    // Links this domain to the Bursa Bull Charge entity Google already knows.
    sameAs: BRAND_SAME_AS,
    contactPoint: [
      {
        "@type": "ContactPoint",
        // Must come from Google's fixed enumeration (customer service, technical
        // support, billing support, sales, …). "organizer" is valid free-text
        // schema.org but Google ignores the whole ContactPoint when it can't map
        // the type.
        contactType: "customer service",
        email: ORG_CONTACT_EMAIL,
        availableLanguage: ["English", "Malay"],
      },
    ],
  };
}

/** schema.org/WebSite — enables sitelinks / site identity for the domain. */
export function websiteLd(): Json {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    name: SITE_NAME,
    alternateName: BRAND_ALTERNATE_NAMES,
    url: SITE_URL,
    publisher: { "@id": ORG_ID },
  };
}

/** schema.org/SportsEvent — the race itself (the key entity for GEO/event queries). */
export function sportsEventLd(): Json {
  return {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    "@id": EVENT_ID,
    name: SITE_NAME,
    // The alias most likely to match a real query ("BBC rat race") — this is the
    // node a search engine resolves an event-shaped question against.
    alternateName: BRAND_ALTERNATE_NAMES,
    sameAs: BRAND_SAME_AS,
    description: SITE_DESCRIPTION,
    startDate: EVENT_START_ISO,
    endDate: EVENT_END_ISO,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    image: [DEFAULT_OG_IMAGE],
    url: SITE_URL,
    sport: "Corporate relay running",
    location: {
      "@type": "Place",
      name: EVENT_VENUE_NAME,
      address: {
        "@type": "PostalAddress",
        streetAddress: EVENT_STREET_ADDRESS,
        addressLocality: EVENT_LOCALITY,
        postalCode: EVENT_POSTAL_CODE,
        addressCountry: EVENT_COUNTRY,
      },
    },
    // Keep in step with the `organisers` tier in pages/sponsors/data.ts — the
    // Sponsors page renders these four, so the structured data must agree.
    organizer: [
      { "@type": "Organization", name: "Bursa Malaysia", url: "https://www.bursamalaysia.com" },
      {
        "@type": "Organization",
        name: "Yayasan Bursa Malaysia",
        url: "https://www.bursamalaysia.com/about_bursa/sustainability/yayasan-bursa-malaysia/overview",
      },
      { "@type": "Organization", name: "The Edge Malaysia", url: "https://theedgemalaysia.com" },
      {
        "@type": "Organization",
        name: "The Edge Education Foundation",
        url: "https://teef.org.my/",
      },
    ],
    // NO `offers` block, deliberately. Google requires `price` AND `priceCurrency`
    // inside an Offer, and this event publishes no registration price anywhere on
    // the site (the only prices are sponsorship tiers, which are a different offer).
    // The previous block also pointed `offers.url` at /registration, an auth-gated
    // page, so Google could not verify it was a ticketing page. Net effect was two
    // permanent Rich Results warnings and an offer that contributed nothing.
    // If a public registration fee is ever published, add offers with price +
    // priceCurrency + validFrom and point url at a crawlable page.
  };
}

/**
 * schema.org/WebPage for a specific page. `breadcrumb` items are
 * `[{ name, path }]` ordered from Home → current page.
 */
export function webPageLd(opts: {
  name: string;
  description: string;
  path: string;
  breadcrumb?: Array<{ name: string; path: string }>;
}): Json {
  const page: Json = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: opts.name,
    description: opts.description,
    url: absoluteUrl(opts.path),
    isPartOf: { "@id": WEBSITE_ID },
    about: { "@id": ORG_ID },
  };
  if (opts.breadcrumb?.length) {
    page.breadcrumb = breadcrumbLd(opts.breadcrumb);
  }
  return page;
}

/**
 * schema.org/ItemList of sponsor Organizations.
 *
 * The sponsor wall is pure imagery, so without this no search or AI crawler
 * can learn WHO backs the event — and GEO extraction is an explicit goal for
 * this site. Order matches the on-page tier order.
 */
export function sponsorsLd(items: Array<{ name: string; href: string }>): Json {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `Sponsors — ${SITE_NAME}`,
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Organization",
        name: item.name,
        url: item.href,
      },
    })),
  };
}

/**
 * schema.org/ItemList of VideoObjects — the sponsor promo videos.
 *
 * Same reason as `sponsorsLd`: the page is a grid of thumbnails, so this is the
 * only way a crawler learns a video exists, who it is about and how long it
 * runs. `durationSec` is converted to the ISO-8601 form schema.org expects
 * (PT1M34S); `uploadedIso` is the date we published it, not the shoot date.
 */
export function sponsorVideosLd(
  items: Array<{
    id: string;
    name: string;
    description: string;
    contentUrl: string;
    thumbnailUrl?: string;
    durationSec: number;
    uploadedIso: string;
  }>,
  pagePath: string
): Json {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `Sponsor videos — ${SITE_NAME}`,
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "VideoObject",
        "@id": `${absoluteUrl(pagePath)}#video-${item.id}`,
        name: item.name,
        description: item.description,
        contentUrl: absoluteUrl(item.contentUrl),
        ...(item.thumbnailUrl ? { thumbnailUrl: absoluteUrl(item.thumbnailUrl) } : {}),
        duration: isoDuration(item.durationSec),
        uploadDate: item.uploadedIso,
        isPartOf: { "@id": WEBSITE_ID },
        about: { "@id": ORG_ID },
      },
    })),
  };
}

/** Seconds → ISO-8601 duration (schema.org's format), e.g. 94 → "PT1M34S". */
function isoDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.round(totalSeconds));
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `PT${minutes > 0 ? `${minutes}M` : ""}${rest > 0 || minutes === 0 ? `${rest}S` : ""}`;
}

/** schema.org/BreadcrumbList from ordered `[{ name, path }]` items. */
export function breadcrumbLd(items: Array<{ name: string; path: string }>): Json {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}
